import { useNavigate } from "react-router-dom";
import { formatOpeningStockSummary } from "@/lib/opening-stock";
import { channelFailureLines, type ChannelOutcome } from "@/lib/wizard-post-create";

export interface CreatedStockRow {
  locationId: string;
  locationName: string;
  qty: number;
  variantSku: string | null;
}

export interface CreatedResult {
  productId: string;
  name: string;
  productType: "simple" | "variation" | "bundle";
  variantCount: number;
  sku: string | null;
  openingStock: CreatedStockRow[];
  channelFailures?: string[];
  /**
   * Structured per-account outcomes. Preferred over `channelFailures` because
   * two accounts can share a display name and identical error text.
   */
  channelOutcomes?: ChannelOutcome[];
}

interface SuccessScreenProps {
  result: CreatedResult;
  onAddAnother: () => void;
  /** Set when the product was created but a supplier link failed to persist. */
  supplierWarning?: string | null;
  onRetrySupplier?: () => void;
  retryingSupplier?: boolean;
  /** Images whose upload/assignment failed during creation (filename + reason). */
  mediaFailures?: { id?: string; name: string; reason: string; source?: "draft_transfer" | "channel_media" }[];
  /** Retries only the channel steps that are still unresolved. */
  onRetryChannels?: () => void;
  retryingChannels?: boolean;
  /** Operator-facing message from a recovery action (e.g. a retry that was rejected). */
  notice?: string | null;
}

export default function SuccessScreen({ result, onAddAnother, supplierWarning, onRetrySupplier, retryingSupplier, mediaFailures = [], onRetryChannels, retryingChannels = false, notice }: SuccessScreenProps) {
  const navigate = useNavigate();
  const totalStock = result.openingStock.reduce((sum, r) => sum + r.qty, 0);
  const locationCount = new Set(result.openingStock.map((row) => row.locationId).filter(Boolean)).size;
  const failedChannelLines = channelFailureLines(result.channelOutcomes ?? []);

  const quantitySummary =
    result.productType === "variation"
      ? `${result.variantCount} ${result.variantCount === 1 ? "variant" : "variants"}`
      : result.productType === "bundle"
        ? "Bundle ready to configure"
        : "1 sellable SKU";

  const stockSummary = formatOpeningStockSummary(result.openingStock);

  /**
   * F-08: an upload failure, a draft-transfer failure and a channel-image
   * failure are three different problems with three different remedies. The
   * shared "could not be uploaded" heading was wrong for a transfer failure —
   * those images WERE uploaded, they just did not reach the product.
   */
  const transferFailures = mediaFailures.filter((row) => row.source === "draft_transfer");
  const uploadFailures = mediaFailures.filter((row) => row.source !== "draft_transfer");
  const onlyTransferFailures = transferFailures.length > 0 && uploadFailures.length === 0;
  const mediaHeading = onlyTransferFailures
    ? (transferFailures.length === 1
      ? "1 image did not transfer to the product"
      : `${transferFailures.length} images did not transfer to the product`)
    : mediaFailures.length === 1
      ? "1 image could not be uploaded"
      : `${mediaFailures.length} images could not be uploaded`;
  const mediaBody = onlyTransferFailures
    ? `The product was created and the images were uploaded to the draft, but ${transferFailures.length === 1 ? "it did not attach" : "they did not attach"} to the product. Add ${transferFailures.length === 1 ? "it" : "them"} again from the product's Media tab.`
    : `The product was created, but ${mediaFailures.length === 1 ? "this image didn't attach" : "these images didn't attach"}. Add them again from the product's Media tab.`;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
          <i className="ri-check-line text-3xl text-emerald-600" />
        </div>
        <h2 className="mt-5 text-2xl font-semibold text-slate-900">Product created successfully</h2>
        <p className="mt-2 text-slate-600">{result.name}</p>
        <p className="mt-2 text-xs text-slate-500">This product exists in Tradivo. Publishing to a sales channel is a separate step from the product page.</p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm text-slate-600">
          <span className="px-3 py-1.5 bg-slate-100 rounded-full font-medium">{quantitySummary}</span>
          {result.sku && <span className="px-3 py-1.5 bg-slate-100 rounded-full font-mono">{result.sku}</span>}
          <span className="px-3 py-1.5 bg-slate-100 rounded-full">{stockSummary}</span>
        </div>

        {totalStock > 0 && (result.openingStock.length > 1 || locationCount > 1) && (
          <ul className="mt-4 text-sm text-slate-500 space-y-1">
            {result.openingStock.map((r, i) => (
              <li key={i}>
                {r.qty} in {r.locationName}
                {r.variantSku && <span className="font-mono text-slate-400"> ({r.variantSku})</span>}
              </li>
            ))}
          </ul>
        )}

        {result.productType === "variation" && totalStock === 0 && (
          <p className="mt-4 text-xs text-slate-400">You can add opening stock for each variant on the product page.</p>
        )}

        {result.channelFailures && result.channelFailures.length > 0 && (
          <div className="mt-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left text-sm text-amber-800">
            <p>The product was created. These channel drafts were not fully saved:</p>
            <ul className="mt-2 space-y-1">
              {result.channelFailures.map((failure, index) => (
                <li key={`${index}-${failure}`}>{failure}</li>
              ))}
            </ul>
          </div>
        )}

        {failedChannelLines.length > 0 && (
          <div className="mt-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <div className="flex items-start gap-2.5">
              <i className="ri-alert-line text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">The product was created. Some channel steps still need attention.</p>
                <ul className="mt-2 space-y-1">
                  {failedChannelLines.map((row) => (
                    <li key={row.key} data-testid={`channel-failure-${row.key}`}>
                      <span className="font-medium">{row.displayName}</span>
                      <span> — {row.heading}</span>
                      {row.message && <span className="text-amber-700">: {row.message}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {onRetryChannels && (
              <button
                onClick={onRetryChannels}
                disabled={retryingChannels}
                className="mt-3 px-3 py-1.5 bg-white border border-amber-300 hover:bg-amber-50 text-amber-800 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
              >
                <i className={retryingChannels ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
                {retryingChannels ? "Retrying..." : "Retry unfinished channel steps"}
              </button>
            )}
          </div>
        )}

        {supplierWarning && (
          <div className="mt-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <div className="flex items-start gap-2.5">
              <i className="ri-alert-line text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">Supplier relationship could not be saved</p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  The product was created, but the link to <strong>{supplierWarning}</strong> didn't save.
                  You can retry now or fix it later on the product's Suppliers tab.
                </p>
              </div>
            </div>
            {onRetrySupplier && (
              <button
                onClick={onRetrySupplier}
                disabled={retryingSupplier}
                className="mt-3 px-3 py-1.5 bg-white border border-amber-300 hover:bg-amber-50 text-amber-800 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
              >
                <i className={retryingSupplier ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
                {retryingSupplier ? "Retrying..." : "Retry supplier link"}
              </button>
            )}
          </div>
        )}

        {notice && (
          <div className="mt-6 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-left text-sm text-slate-700">
            {notice}
          </div>
        )}

        {mediaFailures.length > 0 && (
          <div className="mt-6 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <div className="flex items-start gap-2.5">
              <i className="ri-alert-line text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold">
                  {mediaHeading}
                </p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  {mediaBody}
                </p>
                <ul className="mt-2 space-y-1 text-xs text-amber-800">
                  {mediaFailures.map((f) => (
                    <li key={f.id ?? f.name} className="truncate">
                      <span className="font-medium">{f.name}</span>
                      {f.reason && <span className="text-amber-700"> — {f.reason}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
            onClick={() => navigate(`/products/${result.productId}?tab=channels`)}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-store-2-line" /> List on sales channels
          </button>
          <button
            onClick={() => navigate(`/products/${result.productId}`)}
            className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-eye-line" /> View Product
          </button>
          {result.productType !== "bundle" && (
            <button
              onClick={() => navigate("/purchase-orders?new=1")}
              className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-shopping-cart-line" /> Create Purchase Order
            </button>
          )}
          <button
            onClick={onAddAnother}
            className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" /> Add Another Product
          </button>
        </div>
      </div>
    </div>
  );
}